import React from 'react';
import classnames from 'classnames';
import { useRefReady } from 'signer-app/utils/use-ref-ready';
import {
  signatureRequestContext,
  zoomContext,
  ORIGIN_PAGE_CONTAINER,
  getBoundingBox,
} from 'signer-app/signature-request';
import { signerContext } from 'signer-app/signer-signature-document/context';
import styles from 'signer-app/signer-signature-document/signer-field.module.css';
import { CheckboxField, RadioField } from 'signer-app/types/editor-types';
import { DocumentAddress } from 'signer-app/signature-request/context';
import { useTouchedFieldIds } from 'signer-app/signer-experience/use-validation';

type GroupableFields = CheckboxField | RadioField;

type Props = {
  groupedFields: Record<string, Array<GroupableFields>>;
};

function GroupedFields({ groupedFields }: Props) {
  const { pageContainerRef, selectedFieldIds } = React.useContext(
    signatureRequestContext,
  );
  const { toScreenCoords, textScale } = React.useContext(zoomContext);
  const { validationErrors } = React.useContext(signerContext);
  const readyForPages = useRefReady(pageContainerRef);
  const touchedFieldIds = useTouchedFieldIds();

  // Don't render groups until we have a page container so that positions can be
  // calculated accurately.
  if (!readyForPages) {
    return null;
  }

  const nodes = Object.keys(groupedFields).map((groupId) => {
    const groupReadOnly = groupedFields[groupId].every((f) => f.readOnly);
    if (groupReadOnly) {
      return <></>;
    }

    const convertedFields = groupedFields[groupId].map(
      (f) => toScreenCoords(f, ORIGIN_PAGE_CONTAINER) as DocumentAddress,
    );
    const boundingBox = getBoundingBox(convertedFields);

    const activeField = groupedFields[groupId].find((f) =>
      selectedFieldIds.includes(f.id),
    );

    const margin = Math.min(boundingBox.height, boundingBox.width) * 0.2;

    // All groups have a requirement, but if the lower bound is 0 it isn't
    // really required.
    const requirement = groupedFields[groupId][0].requirement;
    const required = requirement?.indexOf('require_0') === -1;

    const hasValidationError = groupedFields[groupId].some((f) => {
      return touchedFieldIds.has(f.id) && validationErrors[f.id];
    });

    return (
      <div
        style={{
          // The old signer app makes the required * 20px tall, but this needs
          // to account for textScale/zoom
          fontSize: `${20 * textScale}px`,
          left: boundingBox.x - margin,
          top: boundingBox.y - margin,
          width: boundingBox.width + margin * 2,
          height: boundingBox.height + margin * 2,
        }}
        className={classnames(styles.groupBorder, styles.group, {
          [styles.required]: required,
          [styles.active]: activeField != null,
          [styles.error]: hasValidationError,
        })}
        key={groupId}
      />
    );
  });
  return <>{nodes}</>;
}

export default React.memo(GroupedFields);
